import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ChevronDown, Menu, MessageCircle, X } from "lucide-react";
import Logo from "@/components/Logo";
import { SPORTS, PRODUCTS, waLink } from "@/data/content";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const NAV = [
  { key: "home", to: "/", label: "Home" },
  {
    key: "sports",
    to: "/#sports",
    label: "Sports",
    dropdown: SPORTS.map((s) => ({ label: s.name, href: waLink(s.msg), external: true })),
  },
  {
    key: "products",
    to: "/#what-we-make",
    label: "Products",
    dropdown: PRODUCTS.map((p) => ({ label: p, href: "/#what-we-make", external: false })),
  },
  { key: "manufacturing", to: "/manufacturing", label: "Manufacturing" },
  { key: "about", to: "/about", label: "About Us" },
  { key: "contact", to: "/contact", label: "Contact" },
];

function DropItem({ item, d }) {
  const cls =
    "flex items-center justify-between gap-6 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-navy transition-colors";
  const tid = `nav-drop-${item.key}-${slug(d.label)}`;
  if (d.external) {
    return (
      <a href={d.href} target="_blank" rel="noopener noreferrer" data-testid={tid} className={cls}>
        {d.label}
        <MessageCircle size={14} className="text-ice shrink-0" />
      </a>
    );
  }
  return (
    <Link to={d.href} data-testid={tid} className={cls}>
      {d.label}
    </Link>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(null);
  const [mobile, setMobile] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const { pathname } = useLocation();

  useEffect(() => {
    setMobile(false);
    setOpen(null);
  }, [pathname]);

  return (
    <header
      className="fixed inset-x-0 top-0 z-50"
      onMouseLeave={() => setOpen(null)}
      data-testid="site-header"
    >
      <nav className="bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between gap-6">
          <Link to="/" data-testid="nav-logo-link" aria-label="Top Player Sportswear home" className="shrink-0">
            <Logo />
          </Link>
          <div className="hidden xl:flex items-center gap-1">
            {NAV.map((item) =>
              item.dropdown ? (
                <div key={item.key} className="relative" onMouseEnter={() => setOpen(item.key)}>
                  <Link
                    to={item.to}
                    data-testid={`nav-link-${item.key}`}
                    className={`flex items-center gap-1 px-3.5 py-2 text-[13px] font-semibold uppercase tracking-wider rounded-md transition-colors ${
                      open === item.key ? "text-icehover" : "text-slate-700 hover:text-navy"
                    }`}
                  >
                    {item.label}
                    <ChevronDown size={13} className={`transition-transform duration-200 ${open === item.key ? "rotate-180" : ""}`} />
                  </Link>
                  <AnimatePresence>
                    {open === item.key && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 6 }}
                        transition={{ duration: 0.15 }}
                        className="absolute left-0 top-full pt-2"
                        data-testid={`nav-dropdown-${item.key}`}
                      >
                        <div className="bg-white border border-slate-200 rounded-lg shadow-xl py-2 min-w-[230px]">
                          {item.dropdown.map((d) => (
                            <DropItem key={d.label} item={item} d={d} />
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <NavLink
                  key={item.key}
                  to={item.to}
                  data-testid={`nav-link-${item.key}`}
                  className={({ isActive }) =>
                    `px-3.5 py-2 text-[13px] font-semibold uppercase tracking-wider rounded-md transition-colors ${
                      isActive ? "text-icehover" : "text-slate-700 hover:text-navy"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </div>
          <Link to="/contact" data-testid="nav-request-quote-button" className="hidden xl:inline-flex btn-accent shrink-0">
            Request a Quote <ArrowUpRight size={16} />
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            className="xl:hidden text-navy p-2"
            onClick={() => setMobile(!mobile)}
            aria-label="Toggle menu"
          >
            {mobile ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {mobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="xl:hidden fixed inset-x-0 top-[72px] bottom-0 bg-white overflow-y-auto border-t border-slate-200"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-6">
              {NAV.map((item) => (
                <div key={item.key} className="border-b border-slate-100">
                  {item.dropdown ? (
                    <>
                      <button
                        data-testid={`mobile-accordion-${item.key}`}
                        onClick={() => setExpanded(expanded === item.key ? null : item.key)}
                        className="w-full flex items-center justify-between py-4 font-display font-semibold uppercase text-xl text-navy"
                      >
                        {item.label}
                        <ChevronDown size={18} className={`text-slate-400 transition-transform ${expanded === item.key ? "rotate-180" : ""}`} />
                      </button>
                      <AnimatePresence>
                        {expanded === item.key && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.25 }}
                            className="overflow-hidden"
                          >
                            <Link to={item.to} className="block py-2.5 pl-4 text-sm font-semibold text-icehover" data-testid={`mobile-view-all-${item.key}`}>
                              View all {item.label}
                            </Link>
                            {item.dropdown.map((d) =>
                              d.external ? (
                                <a
                                  key={d.label}
                                  href={d.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-testid={`mobile-item-${item.key}-${slug(d.label)}`}
                                  className="flex items-center gap-2 py-2.5 pl-4 text-sm text-slate-600 hover:text-navy"
                                >
                                  {d.label}
                                  <MessageCircle size={13} className="text-ice" />
                                </a>
                              ) : (
                                <Link
                                  key={d.label}
                                  to={d.href}
                                  data-testid={`mobile-item-${item.key}-${slug(d.label)}`}
                                  className="block py-2.5 pl-4 text-sm text-slate-600 hover:text-navy"
                                >
                                  {d.label}
                                </Link>
                              )
                            )}
                            <div className="pb-3" />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <NavLink
                      to={item.to}
                      data-testid={`mobile-link-${item.key}`}
                      className={({ isActive }) =>
                        `block py-4 font-display font-semibold uppercase text-xl ${isActive ? "text-icehover" : "text-navy"}`
                      }
                    >
                      {item.label}
                    </NavLink>
                  )}
                </div>
              ))}
              <Link to="/contact" data-testid="mobile-request-quote-button" className="btn-accent w-full mt-6">
                Request a Quote <ArrowUpRight size={17} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
